import { useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, ListChecks, AlertTriangle, TrendingUp, Lightbulb, Info, ChevronDown } from "lucide-react";
import dayjs from "dayjs";
import { cn } from "../../utils/tailwindUtils";

/**
 * One daily briefing (sales_agent SalesAgentRun.output).
 *
 * The rule-based briefing is always there. When the local AI worker has added
 * a narrative that passed the fact check, it is shown instead, labelled as
 * AI-written; every number in it was matched against the CRM figures.
 */

function Section({ icon: Icon, title, items, tone }) {
    if (!items || items.length === 0) return null;
    return (
        <div>
            <p className={cn("mb-1 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide",
                tone || "text-gray-500 dark:text-gray-400")}>
                <Icon className="h-3 w-3" /> {title}
            </p>
            <ul className="space-y-1">
                {items.map((item, i) => {
                    const text = typeof item === "string" ? item : item.text;
                    const ids = typeof item === "string" ? [] : (item.alert_ids || (item.alert_id ? [item.alert_id] : []));
                    return (
                        <li key={i} className="flex gap-2 text-sm text-gray-700 dark:text-gray-200">
                            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-gray-400" />
                            <span>
                                {text}
                                {ids.map(id => (
                                    <Link key={id} to={`/reports/action-center?alert=${id}`}
                                        className="ml-1 text-xs text-primary hover:underline">#{id}</Link>
                                ))}
                            </span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default function BriefingView({ run, compact = false }) {
    const [showWarnings, setShowWarnings] = useState(false);
    if (!run) return null;
    const out = run.output || {};
    const ai = out.narrative;
    const body = ai || out;
    const recs = (ai ? ai.recommendations : out.recommendations) || [];
    const warnings = run.warnings || [];
    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-400">
                {ai ? (
                    <span className="inline-flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 font-medium text-primary">
                        <Sparkles className="h-3 w-3" /> AI-written, figures checked against CRM data
                    </span>
                ) : (
                    <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Rule-based
                    </span>
                )}
                {run.status === "queued" && <span>AI narrative waiting for the AI worker</span>}
                {run.status === "running" && <span>AI narrative being written…</span>}
                <span>For {dayjs(run.period_date).format("ddd DD MMM")}</span>
                {run.data_as_of && <span>data as of {dayjs(run.data_as_of).format("hh:mm A")}</span>}
            </div>
            <p className="text-sm leading-relaxed text-gray-800 dark:text-gray-100">{body.summary || out.summary}</p>
            <div className={cn("grid gap-4", !compact && "md:grid-cols-2")}>
                <Section icon={ListChecks} title="Today's priorities" items={body.priorities} tone="text-rose-600 dark:text-rose-300" />
                <Section icon={AlertTriangle} title="Critical risks" items={body.risks} tone="text-amber-600 dark:text-amber-300" />
                <Section icon={Info} title="Yesterday" items={body.yesterday} />
                <Section icon={TrendingUp} title="Team highlights" items={body.highlights} />
            </div>
            {!compact && <Section icon={Lightbulb} title="Recommended actions" items={recs} tone="text-primary" />}
            {ai && ai.uncertainty?.length > 0 && <Section icon={Info} title="What the data cannot tell" items={ai.uncertainty} />}
            {warnings.length > 0 && (
                <div>
                    <button type="button" onClick={() => setShowWarnings(v => !v)}
                        className="flex items-center gap-1 text-[11px] text-gray-400 hover:text-gray-600">
                        <ChevronDown className={cn("h-3 w-3 transition-transform", showWarnings && "rotate-180")} />
                        {warnings.length} note{warnings.length > 1 ? "s" : ""} about this briefing
                    </button>
                    {showWarnings && (
                        <ul className="mt-1 space-y-0.5 text-[11px] text-gray-500">
                            {warnings.map((w, i) => <li key={i}>• {w}</li>)}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
}
